import { Alert, AlertDescription, AlertTitle } from "components/ui/alert";
import { Button } from "components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "components/ui/card";
import { Checkbox } from "components/ui/checkbox";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Skeleton } from "components/ui/skeleton";
import { Spinner } from "components/ui/spinner";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import {
  fetchBatchesDefinitions,
  ReactQueryQueryError,
} from "data/queries-common";
import _ from "lodash";
import { ChevronDown } from "lucide-react";
import { BatchConfig } from "pages/geoscape-batch/models";
import {
  BatchConfigProps,
  StepperControlProps,
} from "pages/geoscape-batch/types";
import {
  batchOutputGeocode,
  batchOutputReverseGeocode,
} from "pages/geoscape-batch/utils";
import { useQuery } from "@tanstack/react-query";
import { resolveIdentityId } from "utils/identity";
import { cn } from "@/lib/utils";
import { TruncatedText } from "components/shared/truncated-text";
import { BatchJobTypeCard } from "./batch-job-type-card";

interface DefineBatchJobProps {}

export const DefineBatchJob = (
  props: DefineBatchJobProps & BatchConfigProps & StepperControlProps,
) => {
  const [identityState] = useIdentity();

  const identityId = resolveIdentityId(identityState);

  const definitionsQuery = useQuery({
    queryKey: [QueryKeys.batchesDefinitionsKey, identityId],
    queryFn: () => fetchBatchesDefinitions(),
  });

  const selectedJobDefinition =
    definitionsQuery.isSuccess &&
    definitionsQuery.data.create.filter(
      (jobDefinition: any) => jobDefinition.type === props.batchConfig.type,
    )[0];

  if (
    definitionsQuery.isSuccess &&
    selectedJobDefinition &&
    props.batchConfig.parameters.matchType === ""
  ) {
    const selectedJobParameter = selectedJobDefinition.parameters.filter(
      (parameterDefinition: any) => parameterDefinition.name === "matchType",
    )[0];

    if (selectedJobParameter.isRequired) {
      props.setBatchConfig((prev) => ({
        ...prev,
        parameters: {
          ...prev.parameters,
          matchType: selectedJobParameter.values[0],
        },
      }));
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Step 1: Define Batch Job</CardTitle>
        <CardDescription>
          Define the batch job type and parameters
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {definitionsQuery.isPending && <Skeleton className="h-100 w-full" />}

        {definitionsQuery.isError && (
          <Alert variant="destructive">
            <AlertTitle>
              There was an error fetching the batch definitions.
            </AlertTitle>
            <AlertDescription>
              {(definitionsQuery.error as ReactQueryQueryError).message}
            </AlertDescription>
          </Alert>
        )}

        {definitionsQuery.isSuccess && (
          <>
            <div className="space-y-1.5">
              <Label htmlFor="batch-name">
                Job Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="batch-name"
                maxLength={200}
                onChange={(event) =>
                  props.setBatchConfig((prev: BatchConfig) => ({
                    ...prev,
                    displayName: event.target.value,
                  }))
                }
                value={props.batchConfig.displayName}
                required
              />
            </div>

            <div className="flex flex-row gap-4">
              {definitionsQuery.data.create.map(
                (jobDefinition: any, idx: number) => (
                  <BatchJobTypeCard
                    key={`batch-job-type-card-${idx}`}
                    name={jobDefinition.name}
                    subtitle={"XXX"}
                    mandatoryFields={["XXX"]}
                    selected={jobDefinition.type === props.batchConfig.type}
                    onClickHandler={() => {
                      props.setBatchConfig((prev: BatchConfig) => ({
                        ...prev,
                        type: jobDefinition.type,
                        parameters: {
                          matchType: "",
                          stateFilter: [],
                          dataset: [],

                          additionalProperties: [],
                        },
                        output: {
                          format: [],
                        },
                        hubInfo: {
                          ...prev.hubInfo,
                          checkedItems:
                            jobDefinition.type == "addressGeocoder"
                              ? batchOutputGeocode.properties
                              : batchOutputReverseGeocode.properties,
                        },
                      }));
                    }}
                  />
                ),
              )}
            </div>

            <div className="flex flex-row gap-4">
              {selectedJobDefinition &&
                selectedJobDefinition.parameters.map(
                  (selectedJobParameter: any) => {
                    const label = _.startCase(selectedJobParameter.name);
                    const currentValue =
                      props.batchConfig.parameters[selectedJobParameter.name];

                    return (
                      <div
                        className="flex flex-col gap-1.5 flex-1"
                        key={selectedJobParameter.name}
                      >
                        <Label htmlFor={`param-${selectedJobParameter.name}`}>
                          {label}
                          {selectedJobParameter.isRequired && (
                            <span className="text-destructive"> *</span>
                          )}
                        </Label>
                        {selectedJobParameter.isMultiple ? (
                          <MultiSelectParameter
                            paramName={selectedJobParameter.name}
                            values={selectedJobParameter.values}
                            selected={currentValue as string[]}
                            onChange={(newValue) =>
                              props.setBatchConfig((prev: BatchConfig) => ({
                                ...prev,
                                parameters: {
                                  ...prev.parameters,
                                  [selectedJobParameter.name]: newValue,
                                },
                                hubInfo: {
                                  ...prev.hubInfo,
                                  sample: {
                                    ...prev.hubInfo.sample,
                                    request: [],
                                    response: [],
                                  },
                                },
                              }))
                            }
                          />
                        ) : (
                          <Select
                            value={currentValue as string}
                            onValueChange={(value) =>
                              props.setBatchConfig((prev: BatchConfig) => ({
                                ...prev,
                                parameters: {
                                  ...prev.parameters,
                                  [selectedJobParameter.name]: value,
                                },
                                hubInfo: {
                                  ...prev.hubInfo,
                                  sample: {
                                    ...prev.hubInfo.sample,
                                    request: [],
                                    response: [],
                                  },
                                },
                              }))
                            }
                          >
                            <SelectTrigger
                              id={`param-${selectedJobParameter.name}`}
                            >
                              <SelectValue placeholder={`Select ${label}`}>
                                {currentValue
                                  ? _.startCase(currentValue as string)
                                  : ""}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {selectedJobParameter.values.map(
                                (selectedJobParameterValue: string) => (
                                  <SelectItem
                                    key={selectedJobParameterValue}
                                    value={selectedJobParameterValue}
                                  >
                                    {_.startCase(selectedJobParameterValue)}
                                  </SelectItem>
                                ),
                              )}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    );
                  },
                )}
            </div>

            <div className="flex flex-row justify-end gap-4">
              <Button
                onClick={() => {
                  props.saveBatchConfig(
                    props.batchConfig,
                    Math.min(props.activeStep + 1, props.maxSteps),
                  );
                }}
                disabled={
                  props.isSaveBatchConfigLoading ||
                  !props.batchConfig.displayName.trim()
                }
              >
                {props.isSaveBatchConfigLoading && <Spinner />}
                Continue
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

interface MultiSelectParameterProps {
  paramName: string;
  values: string[];
  selected: string[];
  onChange: (next: string[]) => void;
}

const MultiSelectParameter = ({
  paramName,
  values,
  selected,
  onChange,
}: MultiSelectParameterProps) => {
  const toggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const displayValue =
    selected.length > 0
      ? selected.map((v) => _.startCase(v)).join(", ")
      : `Select ${_.startCase(paramName)}`;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "border-input data-placeholder:text-muted-foreground bg-input/20 dark:bg-input/30 dark:hover:bg-input/50 focus-visible:border-ring focus-visible:ring-ring/30 gap-1.5 rounded-md border px-2 py-1.5 text-xs/relaxed transition-colors focus-visible:ring-2 h-7 flex w-full items-center justify-between whitespace-nowrap outline-none disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
          )}
        >
          <TruncatedText
            as="span"
            className="min-w-0 text-left"
            tooltip={displayValue}
          >
            {selected.length > 0 ? (
              displayValue
            ) : (
              <span className="text-muted-foreground">{displayValue}</span>
            )}
          </TruncatedText>
          <ChevronDown className="text-muted-foreground size-3.5 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-(--radix-popover-trigger-width) p-2"
      >
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {values.map((value) => (
            <div key={value} className="flex items-center gap-2">
              <Checkbox
                id={`${paramName}-${value}`}
                checked={selected.includes(value)}
                onCheckedChange={() => toggle(value)}
              />
              <Label
                htmlFor={`${paramName}-${value}`}
                className="text-sm font-normal cursor-pointer"
              >
                {_.startCase(value)}
              </Label>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
